'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { ArrowUpRight } from 'lucide-react';

export function Testimonials() {
  const t = useTranslations('landing.testimonials');
  const [activeIndex, setActiveIndex] = useState(0);

  const testimonials = [
    {
      quote:
        "Bringtable completely standardized our hiring process. The AI interviewer provides us with objective, highly-structured evidence that we simply couldn't get from standard technical screens.",
      author: 'Sarah Jenkins',
      role: 'VP of Engineering',
      company: 'TechFlow',
      image:
        'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&q=80&w=800',
      logo: (
        <div className="flex items-center gap-2">
          <div className="w-5 h-5 rounded-sm bg-white" />
          <span className="font-bold text-white tracking-tight">TechFlow</span>
        </div>
      ),
    },
    {
      quote:
        "The realism of the AI is mind-blowing. Candidates actually forget they're talking to a machine after the first two minutes. It's transformed how we screen at scale.",
      author: 'Marcus Chen',
      role: 'Head of Talent',
      company: 'ScaleDynamics',
      image:
        'https://images.unsplash.com/photo-1556157382-97eda2d62296?auto=format&fit=crop&q=80&w=800',
      logo: (
        <div className="flex items-center gap-2 text-white font-serif font-bold italic tracking-wider">
          ScaleDynamics
        </div>
      ),
    },
    {
      quote:
        'We reduced our time-to-hire by 40% while simultaneously increasing the quality of our on-site interviews. The signal-to-noise ratio is incredible.',
      author: 'Elena Rodriguez',
      role: 'Dir. of Engineering',
      company: 'CloudNative',
      image:
        'https://images.unsplash.com/photo-1580489944761-15a19d654956?auto=format&fit=crop&q=80&w=800',
      logo: (
        <div className="flex items-center gap-1.5 font-bold text-white uppercase tracking-widest text-sm">
          <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
            <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
          </svg>
          CloudNative
        </div>
      ),
    },
  ];

  return (
    <section
      id="case-studies"
      data-theme="dark"
      className="py-[120px] relative overflow-hidden bg-black rounded-t-[2rem] -mt-8 z-[50]"
    >
      <div className="container max-w-[1440px] mx-auto px-4 relative z-10">
        <div className="mb-16">
          <h2 className="text-4xl md:text-5xl lg:text-[64px] font-serif text-white mb-4 leading-[1.1] tracking-tight">
            {t('headline')}
          </h2>
          <p className="text-2xl md:text-3xl text-white/40 font-serif leading-tight">
            {t('subheadline')}
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-4 h-[600px] w-full">
          {testimonials.map((testimonial, index) => {
            const isActive = activeIndex === index;

            return (
              <div
                key={index}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => setActiveIndex(index)}
                className={`group relative overflow-hidden rounded-3xl transition-all duration-700 ease-[cubic-bezier(0.25,1,0.5,1)] cursor-pointer flex-shrink-0 ${
                  isActive ? 'lg:flex-[2.5] flex-[2.5]' : 'lg:flex-1 flex-[0.5]'
                }`}
              >
                {/* Background Image */}
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={testimonial.image}
                  alt={testimonial.author}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                />

                {/* Dark Gradient Overlay for text readability */}
                <div
                  className={`absolute inset-0 transition-opacity duration-700 ${
                    isActive
                      ? 'bg-gradient-to-t from-black/90 via-black/40 to-black/10'
                      : 'bg-gradient-to-t from-black/80 via-black/20 to-black/10'
                  }`}
                />

                {/* Top Elements */}
                <div className="absolute top-6 left-6 z-20 transition-all duration-500">
                  {testimonial.logo}
                </div>

                {isActive && (
                  <div className="absolute top-6 right-6 z-20 text-white/60 hover:text-white transition-colors">
                    <ArrowUpRight className="w-6 h-6" />
                  </div>
                )}

                {/* Bottom Content */}
                <div className="absolute bottom-0 left-0 w-[600px] p-6 md:p-8 z-20 flex flex-col justify-end h-full">
                  <div
                    className={`transition-all duration-700 ease-[cubic-bezier(0.25,1,0.5,1)] ${isActive ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0 pointer-events-none'}`}
                  >
                    <p className="text-white/90 text-lg md:text-xl leading-relaxed mb-6">
                      &ldquo;{testimonial.quote}&rdquo;
                    </p>
                  </div>

                  <div className="flex items-center gap-2 whitespace-nowrap">
                    <span className="font-semibold text-white">{testimonial.author}</span>
                    <span
                      className={`text-white/60 transition-opacity duration-500 ${isActive ? 'opacity-100' : 'opacity-0 lg:opacity-100'}`}
                    >
                      · {testimonial.role}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function TestimonialsSection() {
  return (
    <section>
      <div>
        <h2>For Candidates</h2>
        <blockquote>
          Bringtable helped me walk into panel interviews prepared — I knew exactly what to expect
          and how to answer confidently.
        </blockquote>
      </div>
      <div>
        <h2>For Hiring Teams</h2>
        <blockquote>
          Bringtable gave our team a consistent signal across every candidate — no more guesswork or
          inconsistent feedback.
        </blockquote>
      </div>
    </section>
  );
}
